// src/components/BatchMessageModal.jsx
// Send a single message to multiple selected tenants at once
import React, { useState } from 'react';
import { fillTemplate, TEMPLATE_LABELS, SMS_TEMPLATE_KEYS } from '../utils/messageTemplates';
import { formatCurrency } from '../utils/formatters';
import { getSmsBlockReason } from '../utils/compliance';

export default function BatchMessageModal({ tenants, propertyName, onClose, onSend }) {
  const [selectedTemplate, setSelectedTemplate] = useState('firm');
  const [lang, setLang] = useState('en');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [progress, setProgress] = useState(0);
  const [errors, setErrors] = useState([]);

  // Preview message using the first tenant as a sample
  const sampleTenant = tenants[0] || {};
  const previewMessage = fillTemplate(selectedTemplate, sampleTenant, lang, { property: propertyName });
  const blockedCount = tenants.filter((tenant) => getSmsBlockReason(tenant)).length;

  const handleSend = async () => {
    if (sending || !tenants.length) return;
    setSending(true);
    setProgress(0);
    setErrors([]);

    let count = 0;
    const failures = [];
    for (const tenant of tenants) {
      const message = fillTemplate(selectedTemplate, tenant, lang, { property: propertyName });
      try {
        await onSend(tenant, message, selectedTemplate, lang);
      } catch (err) {
        console.error('Failed to send to', tenant.name, err);
        failures.push(`${tenant.name}: ${err.message}`);
      }
      count++;
      setProgress(Math.round((count / tenants.length) * 100));
    }

    setErrors(failures);
    setSent(true);
    setTimeout(() => onClose(), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">

        {/* Header */}
        <div className="p-5 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-gray-900">Batch Outreach</h2>
            <p className="text-sm text-gray-500 mt-0.5">Sending to {tenants.length} tenant{tenants.length !== 1 ? 's' : ''}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
        </div>

        {sent ? (
          <div className="p-8 text-center">
            <div className="text-4xl mb-3">✅</div>
            <p className="font-semibold text-green-700">Messages sent to {tenants.length} tenants!</p>
            <p className="text-sm text-gray-500 mt-1">All logged to outreach history.</p>
          </div>
        ) : (
          <div className="p-5 space-y-5">

            {/* Recipient list */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                Recipients ({tenants.length})
              </label>
              <div className="max-h-32 overflow-y-auto space-y-1.5 bg-gray-50 rounded-xl p-3">
                {tenants.map(t => (
                  <div key={t.id} className="flex items-center justify-between text-sm">
                    <span className="font-medium text-gray-800">{t.name} <span className="text-gray-400 font-normal">· Unit {t.unit}</span></span>
                    <span className="text-red-500 font-medium">{formatCurrency(t.currentBalance)}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Template selector */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Message Template</label>
              <div className="flex flex-wrap gap-2">
                {SMS_TEMPLATE_KEYS.map(key => (
                  <button key={key} onClick={() => setSelectedTemplate(key)}
                    className={"px-3 py-1.5 rounded-lg text-sm font-medium transition-colors " +
                      (selectedTemplate === key ? 'bg-tenafi-green text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200')}>
                    {TEMPLATE_LABELS[key]}
                  </button>
                ))}
              </div>
            </div>

            {/* Language */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Language</label>
              <div className="flex gap-2">
                <button onClick={() => setLang('en')}
                  className={"px-4 py-1.5 rounded-lg text-sm font-medium transition-colors " +
                    (lang === 'en' ? 'bg-tenafi-green text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200')}>
                  🇺🇸 English
                </button>
                <button onClick={() => setLang('es')}
                  className={"px-4 py-1.5 rounded-lg text-sm font-medium transition-colors " +
                    (lang === 'es' ? 'bg-tenafi-green text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200')}>
                  🇲🇽 Español
                </button>
              </div>
            </div>

            {/* Preview */}
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                Message Preview <span className="normal-case font-normal">(using {sampleTenant.name || 'first tenant'} as sample)</span>
              </label>
              <div className="bg-gray-100 rounded-2xl p-3 max-w-xs">
                <div className="bg-gray-300 rounded-2xl rounded-bl-sm px-3 py-2 text-sm text-gray-800 leading-snug">
                  {previewMessage}
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-1">Each tenant will receive a personalized version with their own name and balance.</p>
              <p className="text-xs text-gray-400 mt-1">STOP/ALTO opt-out text and secure payment links are appended automatically when needed.</p>
            </div>

            {/* Progress bar (while sending or just finished) */}
            {sending && (
              <div>
                <div className="flex justify-between text-xs text-gray-500 mb-1">
                  <span>{sent ? 'Complete!' : 'Sending...'}</span><span>{progress}%</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-tenafi-green rounded-full transition-all duration-300" style={{ width: progress + '%' }} />
                </div>
              </div>
            )}

            {blockedCount > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-800">
                {blockedCount} recipient{blockedCount !== 1 ? 's are' : ' is'} currently blocked by quiet hours or SMS opt-out settings and will fail until that is resolved.
              </div>
            )}

            {errors.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700 space-y-1">
                {errors.slice(0, 4).map((error) => <p key={error}>{error}</p>)}
                {errors.length > 4 && <p>...and {errors.length - 4} more.</p>}
              </div>
            )}
          </div>
        )}

        {!sent && (
          <div className="p-5 border-t border-gray-100 flex items-center justify-between gap-3">
            <p className="text-xs text-gray-400">
              ⚠️ Each message will be individually personalized before sending.
            </p>
            <div className="flex gap-2">
              <button onClick={onClose} className="px-4 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-800">Cancel</button>
              <button onClick={handleSend} disabled={sending || !tenants.length}
                className="px-5 py-2.5 bg-tenafi-green text-white rounded-xl text-sm font-semibold hover:bg-tenafi-green-dark transition-colors disabled:opacity-50">
                {sending ? 'Sending...' : `Send to ${tenants.length} Tenants`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
